import { memo, useState, useCallback, FC, useEffect, useMemo, useRef } from 'react';
import { useJsApiLoader, GoogleMap, PolygonF, Marker } from '@react-google-maps/api';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMapContext } from '../../context/MapContext';
import { useProjectContext } from '../../context/ProjectContext';
import Loading from '../Loading';
import SearchContainer from './SearchContainer';
import { libraries, polygonOptions } from './MapHelpers';
import useTerraDrawMarkers from './useTerraDrawMarkers';
import useMapInteractions from './useMapInteractions';
import useMapIndexPolygon from './useMapIndexPolygon';
import useMarketDrawing from './useMarketDrawing';
import useAreaPolygon from '@/api/mutations/useAreaPolygon';
import { GeocodeType } from '@/api/mutations/useAreaPolygon';
import { calculateBounds, getTodayDateISO } from '@/helpers';
import { CoordinatesType } from '@/types/dataTypes';
const StyledMap = styled.div`
  ${tw`flex relative h-full w-full md:rounded-xl`}

  .googleMap {
    ${tw`md:rounded-xl`}
  }

  .gm-style-mtc,
  .gm-style-cc,
  .gm-control-active,
  .gm-control-zoom-out {
    ${tw`hidden`}
  }
`;

interface Props {
    isSearchHidden?: boolean;
}

interface CoordsType {
    lat: number;
    lng: number;
}

interface MapMarkersType {
    coords: CoordsType;
    name: string;
    area_ids: number;
}
const mapId = import.meta.env.VITE_API_GOOGLE_MAPS_ID;

const Map: FC<Props> = ({ isSearchHidden }) => {
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: import.meta.env.VITE_API_GOOGLE_MAPS_API_KEY,
        libraries: libraries,
        language: 'sv',
        version: 'quarterly',
    });
    const [mapShapes, setMapShapes] = useState<google.maps.Data[]>([]);
    const [mapMarkers, setMapMarkers] = useState<MapMarkersType[]>([]);

    const memoizedMapMarkers = useMemo(() => {
        return mapMarkers;
    }, [mapMarkers]);

    const { data, mutate: areaPolygonMutation, isPending: isAreaPolygonPending } = useAreaPolygon();

    const [autocomplete, setAutocomplete] =
        useState<google.maps.places.Autocomplete | null>(null);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const { selectedTab, setIsAdvancedFilterOpen, isAdvancedFilterOpen, showPropertyLines } = useProjectContext();
    const { isStreetViewOn, zoom, mapTypeId, currentCoordinates, setCoordinates } = useMapContext();
    const [currentZoom, setCurrentzoom] = useState(0);
    const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const centerMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

    // Determine if polygon editing should be disabled (Price Development tab)
    const isPolygonEditingDisabled = selectedTab === 'priceDevelopment';

    function getMiddlePoint(coords: any[]): CoordsType {
        let x = 0;
        let y = 0;
        let signedArea = 0;

        for (let i = 0; i < coords.length; i++) {
            const [x0, y0] = coords[i];
            const [x1, y1] = coords[(i + 1) % coords.length];
            const a = x0 * y1 - x1 * y0;
            signedArea += a;
            x += (x0 + x1) * a;
            y += (y0 + y1) * a;
        }

        signedArea /= 2;
        x /= (6 * signedArea);
        y /= (6 * signedArea);
        if (isNaN(x) || isNaN(y)) {
            return { lat: coords[0][0], lng: coords[0][0] }
        }

        return { lat: y, lng: x };
    }

    useEffect(() => {
        if (map && showPropertyLines) {
            fetch();
        }
    }, [map]);
    function getArrayDepth(value: any[]): number {
        return Array.isArray(value) ?
            1 + Math.max(0, ...value.map(getArrayDepth)) :
            0;
    }
    useEffect(() => {
        if (data?.features.length) {
            mapShapes.forEach((buffer) => {
                buffer.setMap(null);
            });
            const shapes: google.maps.Data[] = [];
            const markers: MapMarkersType[] = [];
            data?.features.forEach((items: any) => {
                // Skip items without geometry or coordinates
                if (!items.geometry || !items.geometry.coordinates) {
                    return;
                }
                const depth = getArrayDepth(items.geometry.coordinates);
                const flattenCoords = items.geometry.coordinates.flat(depth - 2);
                const middle = getMiddlePoint(flattenCoords);
                const buffer = new google.maps.Data();
                buffer.addGeoJson(items);
                buffer.setStyle({ fillOpacity: 0.01, clickable: true });
                buffer.addListener("click", (event: google.maps.MouseEvent) => {
                    event.stop();
                    setCoordinates(middle);
                });
                buffer.addListener("mouseover", () => buffer.setStyle({ fillOpacity: 0.2 }));
                buffer.addListener("mouseout", () => buffer.setStyle({ fillOpacity: 0.01, clickable: true }));
                buffer.setMap(map);
                shapes.push(buffer);
                markers.push({
                    coords: { lat: middle.lat, lng: middle.lng },
                    name: items?.properties?.area_display_name,
                    area_ids: items?.properties?.area_ids,
                });
            });
            setMapMarkers(markers);
            setMapShapes(shapes);
        }
    }, [data]);

    const { isDrawModeOn, isDrawingComplete, isBlockingCenterChange, startDrawMode, clearDrawMode } =
        useTerraDrawMarkers({
            map,
            isLoaded,
            selectedTab,
            isPolygonEditingDisabled,
        });

    const { clearMarkers, onPlaceChanged, onMapClick, center } =
        useMapInteractions({
            map,
            isLoaded,
            autocomplete,
            isDrawModeOn,
            isDrawingComplete,
            isBlockingCenterChange,
        });

    const { clearIndexPolygon } = useMapIndexPolygon({
        map,
        selectedTab,
        isPolygonEditingDisabled,
    });

    // Ensure index polygon is cleared when switching away from Price Development
    useEffect(() => {
        if (selectedTab !== 'priceDevelopment' && map) {
            clearIndexPolygon();
        }
    }, [selectedTab, map, clearIndexPolygon]);

    // Create center marker (green dot) using AdvancedMarkerElement
    useEffect(() => {
        // Clear existing center marker
        if (centerMarkerRef.current) {
            centerMarkerRef.current.map = null;
            centerMarkerRef.current = null;
        }

        if (!map || !center || !window.google) {
            return;
        }

        // Create a green circle element for the center marker
        const markerElement = document.createElement('div');
        markerElement.style.width = '20px';
        markerElement.style.height = '20px';
        markerElement.style.borderRadius = '50%';
        markerElement.style.backgroundColor = 'rgb(53, 125, 66)';
        markerElement.style.border = '2px solid white';
        markerElement.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';

        centerMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
            map,
            position: center,
            content: markerElement,
        });

        return () => {
            if (centerMarkerRef.current) {
                centerMarkerRef.current.map = null;
                centerMarkerRef.current = null;
            }
        };
    }, [map, center]);

    const { clearRectangleRef } = useMarketDrawing({
        map,
        isLoaded,
        isDrawModeOn,
        selectedTab,
    });

    const onLoad = useCallback((map: google.maps.Map) => {
        setMap(map);
    }, []);

    const onUnmount = useCallback(() => {
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }
        setMap(null);
        setAutocomplete(null);
        clearMarkers();
        clearDrawMode();
        clearIndexPolygon();
        clearRectangleRef();
    }, [clearMarkers, clearDrawMode, clearIndexPolygon, clearRectangleRef]);

    function fetch(fromCordinates = false) {
        if (!showPropertyLines || isAreaPolygonPending) {
            return;
        }

        const center = map?.getCenter();
        if (center) {
            const cords: CoordinatesType = fromCordinates ? { lat: currentCoordinates.lat, lng: currentCoordinates.lng } : { lat: center.lat(), lng: center.lng() }
            const bounds = calculateBounds(cords, currentZoom > 16 ? 100 : 250);
            const { east, north, south, west } = bounds;

            const cMax = { lat: north, lng: east };
            const cMin = { lat: south, lng: west };

            const obj: GeocodeType = {
                category_ids: 29,
                min_wgs84lat: cMin.lat,
                max_wgs84lat: cMax.lat,
                min_wgs84lon: cMin.lng,
                max_wgs84lon: cMax.lng,
                valid_at: getTodayDateISO(),
                limit: currentZoom > 16 ? 100 : 300
            }
            areaPolygonMutation(obj);
        }
    }

    // Debounced version for drag/zoom events
    const debouncedFetch = useCallback((fromCordinates = false) => {
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }
        fetchTimeoutRef.current = setTimeout(() => {
            fetch(fromCordinates);
        }, 300); // 300ms debounce
    }, [showPropertyLines, isAreaPolygonPending, map, currentCoordinates, currentZoom]);
    useEffect(() => {
        if (map && !isStreetViewOn) {
            map?.getStreetView().setVisible(false)
        }
    }, [isStreetViewOn]);

    useEffect(() => {
        if (showPropertyLines) {
            fetch();
        } else if (mapShapes.length > 0) {
            mapShapes.forEach((buffer) => buffer.setMap(null));
            setMapShapes([]);
        }
    }, [showPropertyLines]);

    useEffect(() => {
        if (showPropertyLines) {
            fetch(true);
        }
    }, [currentCoordinates, showPropertyLines]);

    if (!isLoaded) {
        return <Loading />;
    }

    if (loadError) {
        return <div>Error loading map</div>;
    }
    const handleZoomChanged = () => {
        if (map) {
            const newZoom = Math.floor(map.getZoom() || 0);
            if (newZoom !== currentZoom) {
                setCurrentzoom(newZoom);
            }
        }
    };

    function MarkerObj({ name, coords }: MapMarkersType) {
        return (
            <>
                <Marker position={coords} options={{
                    label: {
                        text: name,
                        color: '#000000', // White color for the label text
                        fontSize: '10px',
                        fontWeight: 'bold',
                    }, optimized: true, icon: {
                        path: google.maps.SymbolPath.CIRCLE, // You can use other paths like FORWARD_OPEN_ARROW, etc.
                        scale: 10,
                        fillColor: '', // Red color
                        fillOpacity: 0,
                        strokeWeight: 0
                    }
                }}
                    onClick={() => setCoordinates(coords)}
                />
            </>

        )
    }

    return (
        <StyledMap>
            <SearchContainer
                id="search-input"
                setStreetView={(value: boolean) => map?.getStreetView().setVisible(value)}
                isDrawModeOn={isDrawModeOn}
                isSearchHidden={isSearchHidden}
                isStreetViewOn={isStreetViewOn}
                selectedTab={selectedTab}
                setAutocomplete={setAutocomplete}
                setIsAdvancedFilterOpen={setIsAdvancedFilterOpen}
                isAdvancedFilterOpen={isAdvancedFilterOpen}
                startDrawMode={startDrawMode}
                onPlaceChanged={onPlaceChanged}
            />
            <GoogleMap
                mapContainerClassName="googleMap"
                center={center}
                mapContainerStyle={{ width: '100%', height: '100%' }}
                onUnmount={onUnmount}
                onLoad={onLoad}
                onDragEnd={() => debouncedFetch(false)}
                onClick={onMapClick}
                zoom={zoom.max}
                onZoomChanged={handleZoomChanged}
                options={{ mapId, zoomControl: true, cameraControl: false, mapTypeId: mapTypeId }}
                clickableIcons={false}>

                {center && memoizedMapMarkers && showPropertyLines && currentZoom > 16 && memoizedMapMarkers.map((item: MapMarkersType, index: number) => {
                    return <MarkerObj key={`${item?.area_ids}-${item?.name}-${index}`} coords={item.coords} name={item.name} />
                })}
            </GoogleMap>
        </StyledMap>
    );
};

export default memo(Map);
